'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Types & Schema
// ============================================================================

const awinFormSchema = z.object({
  advertiserId: z.string().min(1, 'Advertiser ID is required').trim(),
  apiKey: z.string().min(1, 'API Key is required').trim(),
})

type FormValues = z.infer<typeof awinFormSchema>

interface AwinFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function AwinForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: AwinFormProps) {
  const { t } = useTranslation('integrations')

  const form = useForm<FormValues>({
    resolver: zodResolver(awinFormSchema),
    defaultValues: {
      advertiserId: defaultValues.credentials?.advertiserId ?? '',
      apiKey: defaultValues.credentials?.apiKey ?? '',
    },
  })

  const { register, handleSubmit, formState: { errors, touchedFields }, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    const { advertiserId, apiKey } = data
    await onSubmit({
      credentials: {
        advertiserId,
        apiKey,
      },
    })
  }

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">{/* API Credentials */}
      <Card>
        <CardHeader>
          <CardTitle>{t('awin.apiCredentials')}</CardTitle>
          <CardDescription>
            {t('awin.apiCredentialsDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="advertiserId">
              {t('awin.advertiserId')}
              <span className="text-destructive ml-1">*</span>
            </Label>
            <Input
              id="advertiserId"
              placeholder={t('awin.advertiserIdPlaceholder')}
              disabled={isLoading}
              {...register('advertiserId')}
            />
            {errors.advertiserId && touchedFields.advertiserId && (
              <p className="text-xs text-destructive">{t('awin.advertiserIdRequired')}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="apiKey">
              {t('awin.apiKey')}
              <span className="text-destructive ml-1">*</span>
            </Label>
            <Input
              id="apiKey"
              type="password"
              placeholder={t('awin.apiKeyPlaceholder')}
              disabled={isLoading}
              {...register('apiKey')}
            />
            {errors.apiKey && touchedFields.apiKey && (
              <p className="text-xs text-destructive">{t('awin.apiKeyRequired')}</p>
            )}
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
